import {Location} from 'history';
import {Fragment, useCallback, useEffect, useState} from 'react';
import {Prompt} from 'react-router-dom';

import {history} from 'context/history';

type ChildrenProps = {
    open: boolean;
    onClose: (confirm: boolean) => void;
}

type Props = {
    when: boolean | undefined;
    children: (props: ChildrenProps) => React.ReactNode;
    navigate?: (path: string) => void;
    shouldBlockNavigation?: (location: Location) => boolean;
}

export const RouteLeavingGuard: React.FC<Props> = ({
    when,
    children,
    navigate = history.push,
    shouldBlockNavigation = () => true,
}: Props) => {
    const [open, setOpen] = useState(false);
    const [lastLocation, setLastLocation] = useState<Location | null>(null);
    const [confirmedNavigation, setConfirmedNavigation] = useState(false);

    const handleClose = useCallback((confirm: boolean) => {
        setConfirmedNavigation(confirm);
        setOpen(false);
    }, []);

    const handleBlockedNavigation = useCallback((nextLocation: Location): boolean => {
        if (!confirmedNavigation && shouldBlockNavigation(nextLocation)) {
            setOpen(true);
            setLastLocation(nextLocation);
            return false;
        }
        return true;
    }, [confirmedNavigation, shouldBlockNavigation]);

    useEffect(() => {
        if (confirmedNavigation && lastLocation) {
            navigate(lastLocation.pathname);
        }
    }, [confirmedNavigation, lastLocation, navigate]);

    return (
        <Fragment>
            <Prompt when={when} message={handleBlockedNavigation} />
            {children({open, onClose: handleClose})}
        </Fragment>
    );
};
